import { store } from '../store';
import {
  addQuestion,
  addStoredAnswer,
  addAnswer,
  markAnswerError,
  addSpeechRateAlert,
  updateCurrentSpeechRate
} from '../store/conversationSlice';
import {
  setConnectionStatus,
  setConnectionError
} from '../store/connectionSlice';
import { landmineWebSocketService } from './landmineWebSocketService';
import { competitorWebSocketService } from './competitorWebSocketService';
import { sessionManager } from './sessionManager';
import { LiveReconnectScheduler } from './liveReconnect';
import { config } from '../config';
import { validateAndRefreshToken, getIdTokenForced, storeToken } from '../utils/tokenUtils';
import { cardKeyOf } from '../utils/cardAnswerPairing';
import { participantIdOf } from '../utils/participantId';
import { setToken } from '../store/authSlice';

export class WebSocketService {
  private websocket: WebSocket | null = null;
  private url: string | null = null;
  private readonly reconnect = new LiveReconnectScheduler('production', () => this.reconnectToLastUrl());
  private clientId: string;
  private sessionId: string;
  private roomId: string;
  private debugMode: boolean = false; // Set to true to prevent auto-logout on auth failure
  private isReconnecting: boolean = false; // Guard flag to prevent concurrent reconnections
  private authRetryAttempted: boolean = false; // One-shot guard for refresh-and-retry on auth failure
  private serverSentAuthError: boolean = false; // Set by onmessage when an auth_error frame arrives

  constructor() {
    // IDs will be set from session manager when connecting
    this.clientId = '';
    this.sessionId = '';
    this.roomId = '';
    console.log('🧠 Main AI server WebSocket Service initialized');
    // Check if debug mode is enabled via localStorage or URL params
    this.debugMode = localStorage.getItem('salesguru_debug') === 'true' || 
                     new URLSearchParams(window.location.search).get('debug') === 'true';
    if (this.debugMode) {
      console.warn('🐛 Debug mode enabled - auth failures will not auto-logout');
    }
  }

  private getBrowserFingerprint(): string {
    // Create a simple browser fingerprint for client identification
    const userAgent = navigator.userAgent.replace(/\s+/g, '-').toLowerCase();
    const timestamp = Date.now().toString().slice(-6); // Last 6 digits of timestamp
    return `${userAgent.slice(0, 20)}-${timestamp}`;
  }

  private generateGuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

  getSessionInfo() {
    return {
      clientId: this.clientId,
      sessionId: this.sessionId,
      roomId: this.roomId
    };
  }

  private getRoomId(): string {
    // Priority 1: Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const roomParam = urlParams.get('room');
    if (roomParam) {
      const roomId = `room-${roomParam}`.toLowerCase().replace(/[^a-z0-9-]/g, '');
      console.log(`🔗 Using room from URL: ${roomId}`);
      return roomId;
    }
    
    // Priority 2: Check localStorage for manually entered room
    const storedRoom = localStorage.getItem('salesguru_room_id');
    if (storedRoom && storedRoom !== '') {
      console.log(`🔗 Using stored room: ${storedRoom}`);
      return storedRoom;
    }
    
    // Priority 3: Fallback to browser fingerprint
    let machineName = 'browser-default';
    try {
      // Try to get from hostname if available
      if (window.location.hostname && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        machineName = window.location.hostname.split('.')[0];
      } else {
        // Fallback: use a simplified browser fingerprint for room identification
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        ctx!.textBaseline = 'top';
        ctx!.font = '14px Arial';
        ctx!.fillText('Room fingerprint', 2, 2);
        const fingerprint = canvas.toDataURL().slice(-6);
        machineName = `browser-${fingerprint}`;
      }
    } catch {
      machineName = 'browser-default';
    }
    
    const roomId = `room-${machineName}`.replace(/[^a-z0-9-]/g, '').toLowerCase();
    console.log(`🔗 Using fallback room: ${roomId}`);
    
    return roomId;
  }

  async connect(url: string): Promise<void> {
    this.url = url;
    this.reconnect.activate();

    // Guard: Check if already connected to prevent duplicate connections
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      console.log('🔗 Already connected to main WebSocket, skipping duplicate connection');
      return;
    }

    // Clean up any existing connection before creating new one
    if (this.websocket) {
      console.log('🔗 Closing existing WebSocket connection before reconnecting');
      this.websocket.close();
      this.websocket = null;
    }

    // Always pull a fresh token at (re)connect time. Reading from Redux is
    // unsafe — Redux can lag onIdTokenChanged and reconnects during long
    // sessions are exactly when a stale token would surface as auth_error.
    const { token } = await validateAndRefreshToken();

    // disconnect() ran while the token was being fetched: open nothing.
    if (!this.reconnect.isActive()) return;

    if (!token) {
      console.error('❌ No JWT token available for WebSocket connection');
      store.dispatch(setConnectionError('Authentication required'));
      // No socket was opened, so no close event will retry: retry from here.
      this.reconnect.scheduleReconnect();
      throw new Error('No authentication token available');
    }

    return new Promise((resolve, reject) => {
      try {
        // Initialize session if not already done
        if (!sessionManager.hasActiveSession()) {
          sessionManager.initializeSession();
        }

        // Get IDs from session manager for consistency
        const sessionInfo = sessionManager.getSessionInfo();
        this.sessionId = sessionInfo.sessionId || this.generateGuid();
        this.roomId = sessionInfo.roomId || this.sessionId; // Use session ID as room ID for consistency
        this.clientId = sessionInfo.clientId || `react-client-${this.getBrowserFingerprint()}`;

        console.log(`🏠 Main WebSocket Service - Using consistent session IDs:`, {
          sessionId: this.sessionId,
          roomId: this.roomId,
          clientId: this.clientId
        });

        store.dispatch(setConnectionStatus('connecting'));

        // Add token (fetched above) and room_id to WebSocket URL as query parameters
        const separator = url.includes('?') ? '&' : '?';
        const authenticatedUrl = `${url}${separator}token=${encodeURIComponent(token)}&room_id=${encodeURIComponent(this.roomId)}`;

        console.log(`🔗 Connecting to main WebSocket with room_id: ${this.roomId}`);

        const socket = new WebSocket(authenticatedUrl);
        this.websocket = socket;

        socket.onopen = () => {
          if (this.websocket !== socket) return;
          console.log('✅ WebSocket connected to:', url);
          store.dispatch(setConnectionStatus('connected'));
          this.reconnect.markConnected();
          // Successful auth — clear the one-shot retry guard so a future stale
          // token can be retried again.
          this.authRetryAttempted = false;
          this.serverSentAuthError = false;

          // Send test connection message and session config after a small delay
          setTimeout(() => {
            this.sendTestConnection();
            this.sendSessionConfig();
          }, 100);

          // Connect to backend analysis services after main connection is established
          setTimeout(() => {
            // The session ended (or this socket was replaced) in the meantime:
            // starting the detectors now would re-arm their reconnect loops.
            if (this.websocket !== socket) return;
            this.connectToLandmineService();
            this.connectToCompetitorService();
            // Calendar service connection is handled by ConversationPage.tsx for proper lifecycle management
          }, 500);

          resolve();
        };

        socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Check for authentication errors first
            if (data.type === 'auth_error') {
              console.error('🔐 Authentication error from server:', data.data?.error || data.error);
              console.error('🔐 Full auth error data:', data);
              store.dispatch(setConnectionError(data.data?.error || 'Authentication failed'));
              // Mark the failure so the close handler routes to the auth path
              // regardless of what reason string the server sends in its close
              // frame (we previously relied on substring matching, which missed
              // common phrasings like "Invalid or expired token").
              this.serverSentAuthError = true;

              // Close cleanly. JS-initiated close codes are restricted to 1000
              // or 3000-4999; 1008 is server-only and would throw here. 4001
              // is in the application range and unambiguous.
              if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
                this.websocket.close(4001, 'auth-error');
              }
              return;
            }
            this.handleMessage(data);
          } catch (error) {
            console.error('❌ Error parsing WebSocket message:', error);
          }
        };

        socket.onclose = (event) => {
          // A socket this service replaced or closed on purpose (disconnect,
          // reconnectWithNewRoomId) reports nothing and schedules nothing.
          if (this.websocket !== socket) return;
          this.websocket = null;
          console.log('❎ WebSocket connection closed:', event.code, event.reason);
          store.dispatch(setConnectionStatus('disconnected'));

          // Authentication failure (explicit auth_error frame, server-side
          // 1008 close, our own 4001 close from the message handler, or a
          // reason string that names an auth issue) takes the refresh-then-
          // logout path. Every other drop, including a server-side 1000 close
          // (e.g. the idle timeout), is retried while the session is active.
          const reason = (event.reason || '').toLowerCase();
          const isAuthFailure =
            this.serverSentAuthError ||
            event.code === 1008 ||
            event.code === 4001 ||
            (event.code !== 1000 && (
              reason.includes('auth') ||
              reason.includes('unauthorized') ||
              reason.includes('mongodb') ||
              reason.includes('not registered') ||
              reason.includes('invalid') ||
              reason.includes('expired') ||
              reason.includes('token')
            ));
          
          if (isAuthFailure) {
            console.error('🔐 Connection closed due to authentication failure.', {
              code: event.code,
              reason: event.reason,
              url,
              debugMode: this.debugMode,
              retryAlreadyAttempted: this.authRetryAttempted,
            });
            store.dispatch(setConnectionError(event.reason || 'Authentication failed'));

            // One-shot refresh-and-retry: a stale Firebase ID token can cause
            // a single auth_error even when the user is still signed in. Force
            // a refresh, sync Redux + storage, and retry once before treating
            // this as a real session expiry.
            if (!this.authRetryAttempted) {
              this.authRetryAttempted = true;
              console.warn('🔐 Attempting one-shot token refresh before giving up...');
              getIdTokenForced()
                .then((tokenInfo) => {
                  if (!tokenInfo) {
                    throw new Error('Forced refresh returned no token');
                  }
                  storeToken(tokenInfo);
                  store.dispatch(setToken(tokenInfo));
                  // The retry starts the backoff from its first step.
                  this.reconnect.resetBackoff();
                  this.reconnect.scheduleReconnect();
                })
                .catch((err) => {
                  console.error('🔐 Refresh-and-retry failed:', err);
                  this.handleAuthFailureLogout();
                });
              return;
            }

            this.handleAuthFailureLogout();
            return;
          }

          this.reconnect.scheduleReconnect();
        };

        socket.onerror = (error) => {
          console.error('💥 WebSocket error:', error);
          store.dispatch(setConnectionError('Connection failed'));
          reject(new Error('WebSocket connection failed'));
        };

      } catch (error) {
        store.dispatch(setConnectionError('Invalid WebSocket URL'));
        reject(error);
      }
    });
  }

  private handleMessage(data: any) {
    // console.log('📨 Received message:', data);

    // Check if message is for the currently active room
    const messageRoomId = data.room_id || data.data?.room_id;
    const activeRoomId = sessionManager.getRoomId();
    if (messageRoomId && activeRoomId && messageRoomId !== activeRoomId) {
      console.log(`⏭️ Ignoring message for different room: ${messageRoomId} (active: ${activeRoomId})`);
      return;
    }

    switch (data.type) {
      case 'connection_established':
        console.log('🔗 Connection established:', data.data.message);
        break;

      case 'test_connection_ack':
        console.log('✅ Test connection acknowledged:', data.data);
        break;

      case 'question_detected':
        // Filter out non-questions to prevent them from appearing in the conversation feed
        // Note: New Inquiry has relevance_score=0 but should still be shown
        const isLegitimateQuestion = data.data.question &&
                                   data.data.question !== "<Question Not Detected>" &&
                                   data.data.question !== "Question Not Detected" &&
                                   data.data.question.trim() !== "" &&
                                   (data.data.relevance_score > 0 || data.data.category === 'New Inquiry');

        if (isLegitimateQuestion) {
          store.dispatch(addQuestion({
            question: data.data.question,
            category: data.data.category,
            relevance_score: data.data.relevance_score,
            timestamp: data.data.timestamp, // Use backend timestamp from production_final_server.py
            // Who raised it: a line moved to a newcomer takes its cards along.
            participantId: data.data.participantId != null ? String(data.data.participantId) : undefined,
            speakerName: data.data.speakerName || undefined,
            // What its stored and AI answers will name (utils/cardAnswerPairing).
            cardKey: cardKeyOf(data.data) ?? undefined,
          }));
        } else {
          console.log('🔄 Filtered out non-question:', data.data.question);
        }
        break;

      case 'stored_answer_generated':
        console.log('📚 Stored answer received from vector search');
        store.dispatch(addStoredAnswer({
          question: data.data.question,
          stored_answer: data.data.stored_answer,
          timestamp: data.data.timestamp,
          cardKey: cardKeyOf(data.data),
        }));
        break;

      case 'answer_generated':
        store.dispatch(addAnswer({
          question: data.data.question,
          answer: data.data.answer,
          timestamp: data.data.timestamp, // Use backend timestamp from production_final_server.py
          cardKey: cardKeyOf(data.data),
        }));
        break;

      case 'answer_error':
        store.dispatch(markAnswerError({
          question: data.data.question,
          error: data.data.error,
          timestamp: data.data.timestamp, // Use backend timestamp from production_final_server.py
          cardKey: cardKeyOf(data.data),
        }));
        break;

      case 'edit_question_response':
        // Dispatch custom event for edit question response
        const event = new CustomEvent('editQuestionResponse', { detail: data.data });
        window.dispatchEvent(event);
        break;

      case 'speech_rate_alert':
        store.dispatch(addSpeechRateAlert({
          words_per_minute: data.data.words_per_minute,
          // `max_words` is the fallback for a service that has not shipped
          // `wpm_threshold` yet; both carry the same number during rollout.
          wpm_threshold: data.data.wpm_threshold ?? data.data.max_words ?? 190,
          timestamp: new Date().toISOString(), // Use frontend timestamp
          participantId: participantIdOf(data.data?.participantId),
        }));
        // Also update current speech rate when we get an alert
        store.dispatch(updateCurrentSpeechRate({
          words_per_minute: data.data.words_per_minute,
          is_too_fast: true,
        }));
        break;

      case 'speech_rate_settings_updated':
        console.log('✅ Speech rate settings updated:', data.data);
        break;

      case 'transcript':
        // Ignored: the AI server's copy of a customer line the recall socket
        // already delivered (with its speaker id). It arrives behind the room's
        // detection, past the 3 s duplicate window, so lines showed twice
        // (local bot call, 2026-10-03). The desktop apps ignore it too.
        break;

      case 'speech_rate_update':
        store.dispatch(updateCurrentSpeechRate({
          words_per_minute: data.data.words_per_minute,
          is_too_fast: data.data.is_too_fast,
        }));
        break;

      case 'ping':
        // Respond to server ping with pong
        this.sendMessage({
          type: 'pong',
          data: {
            timestamp: new Date().toISOString()
          }
        });
        break;

      case 'pong':
        console.log('🏓 Pong received from server');
        break;

      default:
        console.log('🤷 Unknown message type:', data.type);
    }
  }

  private handleAuthFailureLogout() {
    // Don't attempt further reconnection.
    this.reconnect.stop();

    if (this.debugMode) {
      console.warn('🐛 Debug mode - NOT logging out. You can inspect the error.');
      console.warn('🐛 To disable debug mode, run websocketService.disableDebugMode() in the console.');
      return;
    }

    // The session is over: close every live socket now so none of them keeps
    // a reconnect loop running without a token after the logout. Imported
    // lazily because liveCallSockets imports this module.
    import('./liveCallSockets')
      .then(({ disconnectAllLiveSockets }) => disconnectAllLiveSockets())
      .catch((error) => console.error('🔐 Could not close the live sockets before logout:', error));

    console.warn('🔐 Will logout in 3 seconds... (Run websocketService.enableDebugMode() in the console to prevent auto-logout)');
    setTimeout(() => {
      import('../store/authSlice').then(({ logoutUser }) => {
        console.warn('🔐 Logging out user due to WebSocket authentication failure');
        store.dispatch(logoutUser() as any);
      });
    }, 3000);
  }

  private reconnectToLastUrl(): Promise<void> {
    if (!this.url) {
      return Promise.reject(new Error('Production reconnect has no URL'));
    }
    return this.connect(this.url);
  }

  private sendTestConnection() {
    const testMessage = {
      type: 'test_connection',
      client_id: this.clientId,
      session_id: this.sessionId,
      room_id: this.roomId,
      data: {
        message: 'React UI Connected',
        client_type: 'react-frontend',
        browser: navigator.userAgent,
        timestamp: new Date().toISOString()
      }
    };

    // Send directly without using sendMessage to avoid adding client_id twice
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(testMessage));
      console.log(`📤 Sent test connection message with client ID: ${this.clientId}`);
    } else {
      console.error('❌ WebSocket is not open when trying to send test connection');
    }
  }

  private sendSessionConfig() {
    // Get additional session context from Redux store
    const state = store.getState();
    const firebaseUid = state.auth.user?.uid;
    const isSalespersonHost = state.conversation.isSalespersonHost;
    const sessionInfo = sessionManager.getSessionInfo();

    const sessionConfigMessage = {
      type: 'session_config',
      client_id: this.clientId,
      session_id: this.sessionId,
      room_id: this.roomId,
      data: {
        firebase_uid: firebaseUid,
        bot_id: sessionInfo.roomId, // bot_id is same as room_id in bot sessions
        is_salesperson_host: isSalespersonHost,
        client_type: 'react-frontend',
        session_start_time: sessionInfo.startTime,
        timestamp: new Date().toISOString()
      }
    };

    // Send session configuration to backend services
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      this.websocket.send(JSON.stringify(sessionConfigMessage));
      console.log(`📋 Sent session configuration:`, {
        room_id: this.roomId,
        firebase_uid: firebaseUid,
        is_salesperson_host: isSalespersonHost
      });
    } else {
      console.warn('⚠️ WebSocket is not open when trying to send session config');
    }
  }

  sendMessage(message: any) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) {
      // Add client identification to all messages
      const messageWithId = {
        ...message,
        client_id: this.clientId,
        session_id: this.sessionId,
        room_id: this.roomId
      };
      this.websocket.send(JSON.stringify(messageWithId));
    } else {
      console.error('❌ WebSocket is not connected');
      throw new Error('WebSocket is not connected');
    }
  }

  setRoomId(roomName: string) {
    // Manually set room ID (for debugging/testing)
    if (roomName && roomName.trim()) {
      this.roomId = `room-${roomName.trim()}`.replace(/[^a-z0-9-]/g, '').toLowerCase();
      localStorage.setItem('salesguru_room_id', this.roomId);
      localStorage.setItem('salesguru_room_name', roomName.trim());
      console.log(`🏠 Manually set room ID: ${this.roomId}`);
    }
  }

  getCurrentRoomId(): string {
    return this.roomId;
  }

  private connectToLandmineService() {
    console.log(`⚠️ Attempting to connect to landmine detection service with room ID: ${this.roomId}`);
    landmineWebSocketService.connect().catch((error) => {
      console.warn('⚠️ Could not connect to landmine detection service:', error.message);
      console.warn('⚠️ Landmine detection will be unavailable, but main functionality will continue');
    });
  }

  private connectToCompetitorService() {
    console.log(`🎯 Attempting to connect to competitor detection service with room ID: ${this.roomId}`);
    competitorWebSocketService.connect().catch((error) => {
      console.warn('🎯 Could not connect to competitor detection service:', error.message);
      console.warn('🎯 Competitor detection will be unavailable, but main functionality will continue');
    });
  }

  // Calendar service connection removed - now handled by ConversationPage.tsx
  // This provides better lifecycle management (connect on mount, disconnect on unmount)

  /**
   * Reconnect all WebSocket services with updated room_id from sessionManager
   * This is called when the bot is created and we need to switch to using bot_id as room_id
   */
  async reconnectWithNewRoomId(): Promise<void> {
    // Guard: Prevent concurrent reconnections
    if (this.isReconnecting) {
      console.warn('⚠️ Reconnection already in progress, skipping duplicate request');
      return;
    }

    console.log('🔄 Reconnecting all services with new room_id...');
    this.isReconnecting = true;

    try {
      // Disconnect all services first. connect() below re-activates the
      // reconnect loop for the new room.
      this.reconnect.stop();

      // Disconnect child services
      console.log('⚠️ Disconnecting landmine service for room_id update...');
      landmineWebSocketService.disconnect();

      console.log('🎯 Disconnecting competitor service for room_id update...');
      competitorWebSocketService.disconnect();

      // Calendar service is managed by ConversationPage.tsx, not disconnected here

      // Close main connection
      if (this.websocket) {
        console.log('🔌 Closing main WebSocket for room_id update...');
        this.websocket.close(1000, 'Reconnecting with new room_id');
        this.websocket = null;
      }

      // Wait a bit for connections to close cleanly
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update IDs from session manager
      const sessionInfo = sessionManager.getSessionInfo();
      this.sessionId = sessionInfo.sessionId || this.generateGuid();
      this.roomId = sessionInfo.roomId || this.sessionId;
      this.clientId = sessionInfo.clientId || `react-client-${this.getBrowserFingerprint()}`;

      console.log('🔄 Updated IDs from session manager:', {
        sessionId: this.sessionId,
        roomId: this.roomId,
        clientId: this.clientId
      });

      // Reconnect to main service (which will trigger child services)
      const url = config.server.websocketurl;

      console.log('🔌 Reconnecting main service with new room_id...');
      await this.connect(url);

      console.log('✅ All services reconnected with new room_id');
    } catch (error) {
      console.error('❌ Error during reconnection:', error);
      throw error; // Re-throw to allow caller to handle
    } finally {
      // Always clear the reconnecting flag, even if there was an error
      this.isReconnecting = false;
    }
  }

  disconnect() {
    console.log('🔌 Disconnecting WebSocket service...');

    this.reconnect.stop();

    // Disconnect from landmine service
    console.log('⚠️ Disconnecting landmine service...');
    landmineWebSocketService.disconnect();

    // Disconnect from competitor service
    console.log('🎯 Disconnecting competitor service...');
    competitorWebSocketService.disconnect();

    // Calendar service is managed by ConversationPage.tsx, not disconnected here

    if (this.websocket) {
      console.log('🔌 Closing main WebSocket connection...');
      this.websocket.close(1000, 'User disconnected');
      this.websocket = null;
    }

    store.dispatch(setConnectionStatus('disconnected'));
  }

  isConnected(): boolean {
    return this.websocket !== null && this.websocket.readyState === WebSocket.OPEN;
  }

  // Debug helper methods
  enableDebugMode(): void {
    localStorage.setItem('salesguru_debug', 'true');
    this.debugMode = true;
    console.warn('🐛 Debug mode enabled - auth failures will not auto-logout');
  }

  disableDebugMode(): void {
    localStorage.removeItem('salesguru_debug');
    this.debugMode = false;
    console.log('🐛 Debug mode disabled');
  }
}

export const websocketService = new WebSocketService();

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).websocketService = websocketService;
  console.log('💡 WebSocket service available at window.websocketService');
  console.log('💡 To enable debug mode: window.websocketService.enableDebugMode()');
}
